<script>
  import { onMount } from "svelte";
  import { toasts } from 'svelte-toasts';
  import pb from "../lib/pocketbase";
  import * as XLSX from 'xlsx';
  import RightPane from "../components/Panes/RightPane.svelte";

  // Props
  export const egresoCabItems = null;
  export const egresoId = null;
  
  // State
  let egresoCabs = [];
  let isLoading = false;
  let error = null;
  let selectedItem = null;
  let showRightPane = false;
  
  // Search and filters
  let searchQuery = "";
  let selectedDeliveryMethod = 'all';
  let uniqueDeliveryMethods = [];
  let filteredEgresoCabs = [];
  
  // Date range - Set to current month from 1st to today
  let today = new Date();
  let startDate = new Date(today.getFullYear(), today.getMonth(), 1);
  let endDate = new Date(today);
  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(23, 59, 59, 999);
  
  // Pagination
  let currentPage = 1;
  let itemsPerPage = 10;
  let totalPages = 1;
  let totalItems = 0;
  let paginatedEgresoCabs = [];
  
  // Export state
  let isExporting = false;
  
  // Format dates for input
  function formatDateInput(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  
  let formattedStartDate = formatDateInput(startDate);
  let formattedEndDate = formatDateInput(endDate);
  
  // Status translations and colors
  const statusConfig = {
    NOT_READY: { text: 'No listo', color: 'bg-red-200 border-red-300 text-red-700' },
    PACKING: { text: 'En empaque', color: 'bg-yellow-500 border-yellow-600 text-yellow-900' },
    PARTIALLY_READY: { text: 'Parcialmente listo', color: 'bg-orange-200 border-orange-300 text-orange-700' },
    READY: { text: 'Listo', color: 'bg-teal-200 border-teal-300 text-teal-700' },
    ON_ROUTE: { text: 'En ruta', color: 'bg-lightBlue-200 border-lightBlue-300 text-lightBlue-700' },
    DELIVERED: { text: 'Entregado', color: 'bg-emerald-200 border-emerald-300 text-emerald-700' },
    RETURNED: { text: 'Devuelto', color: 'bg-red-200 border-red-300 text-red-700' },
    CANCELLED: { text: 'Cancelado', color: 'bg-gray-200 border-gray-300 text-gray-700' }
  };
  
  function getStatusText(status) {
    return statusConfig[status]?.text || 'Desconocido';
  }
  
  function getStatusColor(status) {
    return statusConfig[status]?.color || 'bg-gray-100 border-gray-300 text-gray-700';
  }
  
  // Format date for display (convert to Guatemala timezone)
  function formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    // Convert from UTC to Guatemala timezone (UTC-6)
    const guatemalaDate = new Date(date.getTime() - (6 * 60 * 60 * 1000));
    
    // Format date components
    const day = String(guatemalaDate.getUTCDate()).padStart(2, '0');
    const month = String(guatemalaDate.getUTCMonth() + 1).padStart(2, '0');
    const year = guatemalaDate.getUTCFullYear();
    
    // Get hours in 12-hour format
    let hours = guatemalaDate.getUTCHours();
    const ampm = hours >= 12 ? 'p. m.' : 'a. m.';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    
    const minutes = String(guatemalaDate.getUTCMinutes()).padStart(2, '0');
    const seconds = String(guatemalaDate.getUTCSeconds()).padStart(2, '0');
    
    return `${day}/${month}/${year}, ${String(hours).padStart(2, '0')}:${minutes}:${seconds} ${ampm}`;
  }

  // Format timestamp for display (convert to Guatemala timezone)
  function formatTimestampForDisplay(timestampString) {
    if (!timestampString) return '';
    
    try {
      // Create date object from the timestamp (UTC from PocketBase)
      const date = new Date(timestampString);
      if (isNaN(date.getTime())) return timestampString;
      
      // Convert from UTC to Guatemala timezone (UTC-6)
      const guatemalaDate = new Date(date.getTime() - (6 * 60 * 60 * 1000));
      
      // Format as DD/MM/YYYY, HH:MM:SS (24-hour format)
      const day = String(guatemalaDate.getUTCDate()).padStart(2, '0');
      const month = String(guatemalaDate.getUTCMonth() + 1).padStart(2, '0');
      const year = guatemalaDate.getUTCFullYear();
      const hours = String(guatemalaDate.getUTCHours()).padStart(2, '0');
      const minutes = String(guatemalaDate.getUTCMinutes()).padStart(2, '0');
      const seconds = String(guatemalaDate.getUTCSeconds()).padStart(2, '0');
      
      return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return timestampString;
    }
  }

  // Format total time in a readable format (days, hours, minutes, seconds)
  function formatTotalTime(seconds) {
    if (!seconds || seconds === 0) return '0 segundos';
    
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    
    const parts = [];
    
    if (days > 0) {
      parts.push(`${days} día${days !== 1 ? 's' : ''}`);
    }
    if (hours > 0) {
      parts.push(`${hours} hora${hours !== 1 ? 's' : ''}`);
    }
    if (minutes > 0) {
      parts.push(`${minutes} minuto${minutes !== 1 ? 's' : ''}`);
    }
    if (remainingSeconds > 0 || parts.length === 0) {
      parts.push(`${remainingSeconds} segundo${remainingSeconds !== 1 ? 's' : ''}`);
    }
    
    return parts.join(', ');
  }
  
  // Format date for PocketBase
  function formatDateForPB(date) {
    if (!date) return null;
    const d = new Date(date);
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
  }
  
  // Parse personnel fields - handle both JSON arrays and comma-separated strings
  function parseJsonField(field) {
    console.log('parseJsonField input:', field, 'type:', typeof field);
    
    if (!field || field === 'JSON' || field === '""' || field === '') {
      console.log('Field is empty, JSON string, or empty quotes, returning []');
      return [];
    }
    
    if (Array.isArray(field)) {
      console.log('Field is already array:', field);
      return field;
    }
    
    if (typeof field === 'string') {
      // Handle comma-separated strings (like "Alejandro García,Jeferson Cala")
      if (field.includes(',')) {
        const result = field.split(',').map(item => item.trim()).filter(item => item && item !== '""');
        console.log('Parsed comma-separated string:', result);
        return result;
      }
      
      // Handle single values (like "Bayron Salala" or "Diego Chopen")
      if (field.trim() !== '') {
        console.log('Single string value:', field.trim());
        return [field.trim()];
      }
    }
    
    // Try JSON parsing as fallback for other data types
    try {
      const parsed = typeof field === 'string' ? JSON.parse(field) : field;
      console.log('Parsed JSON field:', parsed);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch (error) {
      console.log('Error parsing field:', error, 'returning []');
      return [];
    }
  }

  // Parse status transitions from detalles_estados string - show only last occurrence of each status
  function parseStatusTransitions(detallesEstados) {
    if (!detallesEstados || typeof detallesEstados !== 'string') {
      console.log('No detalles_estados or not a string:', detallesEstados);
      return [];
    }
    
    console.log('Parsing detalles_estados:', detallesEstados);
    
    // Split by pattern: STATUS (TIMESTAMP),STATUS (TIMESTAMP)
    // Use regex to find all STATUS (TIMESTAMP) patterns
    const pattern = /([A-Z_]+)\s*\(([^)]+)\)/g;
    const transitions = [];
    let match;
    
    while ((match = pattern.exec(detallesEstados)) !== null) {
      transitions.push({
        status: match[1].trim(),
        timestamp: match[2].trim()
      });
    }
    
    console.log('Parsed transitions:', transitions);
    
    // Group by status and keep only the last occurrence of each
    const statusMap = new Map();
    transitions.forEach(transition => {
      statusMap.set(transition.status, transition);
    });
    
    // Convert back to array and sort by timestamp (latest first)
    const result = Array.from(statusMap.values()).sort((a, b) => {
      return new Date(b.timestamp) - new Date(a.timestamp);
    });
    
    console.log('Final result:', result);
    return result;
  }
  
  // Load data using the egreso_trazabilidad view
  async function loadData() {
    isLoading = true;
    error = null;
    
    try {
      // Update dates from inputs
      const validStartDate = new Date(formattedStartDate);
      const validEndDate = new Date(formattedEndDate);
      validStartDate.setHours(0, 0, 0, 0);
      validEndDate.setHours(23, 59, 59, 999);
      
      if (isNaN(validStartDate.getTime()) || isNaN(validEndDate.getTime())) {
        throw new Error('Fechas inválidas');
      }
      
      if (validStartDate > validEndDate) {
        throw new Error('La fecha inicial debe ser anterior a la fecha final');
      }
      
      // Try to load from egreso_trazabilidad view first
      console.log('Loading data from egreso_trazabilidad view...');
      
      try {
        // Build filter for the view - use fecha from egresoCab
        const startDateStr = validStartDate.toISOString().split('T')[0];
        const endDateStr = validEndDate.toISOString().split('T')[0];
        
        // Use fecha field from egresoCab (the original date field)
        let filter = `fecha >= "${startDateStr}" && fecha < "${endDateStr}"`;
        
        if (searchQuery.trim()) {
          const search = searchQuery.trim().replace(/[\\"]/g, '\\$&');
          const searchFilter = `numero_pedido ~ "${search}" || nombre ~ "${search}" || observaciones ~ "${search}" || metodo_entrega ~ "${search}"`;
          filter = `(${filter}) && (${searchFilter})`;
        }
        
        if (selectedDeliveryMethod !== 'all') {
          filter = `(${filter}) && metodo_entrega = "${selectedDeliveryMethod}"`;
        }
        
        console.log('Filter:', filter);
        
        const result = await pb.collection('egreso_trazabilidad').getList(currentPage, itemsPerPage, {
          filter,
          sort: '-fecha',
          $autoCancel: false,
          $cache: false  // Force fresh data to get updated peso calculations
        });
        
        egresoCabs = result.items;
        totalPages = result.totalPages;
        totalItems = result.totalItems;
        
        // Fix peso_total by recalculating from egresoDet (since view has duplication issues)
        const egresoDets = await pb.collection('egresoDet').getFullList();
        const egresoDetsByCab = {};
        egresoDets.forEach(det => {
          if (!egresoDetsByCab[det.egresoCab]) {
            egresoDetsByCab[det.egresoCab] = [];
          }
          egresoDetsByCab[det.egresoCab].push(det);
        });

        // Recalculate peso_total for each egreso
        egresoCabs = egresoCabs.map(egreso => {
          const dets = egresoDetsByCab[egreso.id] || [];
          const correctPesoTotal = dets.reduce((sum, det) => sum + ((det.peso || 0) * (det.cantidad || 0)), 0);
          const correctLineasDetalle = dets.length;
          
          return {
            ...egreso,
            peso_total: correctPesoTotal,
            lineas_detalle: correctLineasDetalle
          };
        });
        
        // If no records found, try without date filtering
        if (egresoCabs.length === 0) {
          console.log('No records with date filter, trying without date filter...');
          
          let simpleFilter = '';
          if (searchQuery.trim()) {
            const search = searchQuery.trim().replace(/[\\"]/g, '\\$&');
            simpleFilter = `numero_pedido ~ "${search}" || nombre ~ "${search}" || observaciones ~ "${search}" || metodo_entrega ~ "${search}"`;
          }
          
          if (selectedDeliveryMethod !== 'all') {
            if (simpleFilter) {
              simpleFilter = `(${simpleFilter}) && metodo_entrega = "${selectedDeliveryMethod}"`;
            } else {
              simpleFilter = `metodo_entrega = "${selectedDeliveryMethod}"`;
            }
          }
          
          console.log('Simple filter:', simpleFilter || 'none');
          
          const resultSimple = await pb.collection('egreso_trazabilidad').getList(currentPage, itemsPerPage, {
            filter: simpleFilter || undefined,
            sort: '-fecha',
            $autoCancel: false,
            $cache: false  // Force fresh data to get updated peso calculations
          });
          
          egresoCabs = resultSimple.items;
          totalPages = resultSimple.totalPages;
          totalItems = resultSimple.totalItems;
          
          console.log(`Loaded ${egresoCabs.length} records from egreso_trazabilidad view (no date filter, page ${currentPage}/${totalPages})`);
          
          // Filter by date manually if we got records
          if (egresoCabs.length > 0) {
            const startTime = validStartDate.getTime();
            const endTime = validEndDate.getTime();
            
            egresoCabs = egresoCabs.filter(egreso => {
              const fecha = new Date(egreso.fecha || egreso.created || egreso.updated);
              return fecha.getTime() >= startTime && fecha.getTime() <= endTime;
            });
            
            console.log(`Filtered to ${egresoCabs.length} records by date`);
          }
        }
        
      } catch (viewError) {
        console.warn('egreso_trazabilidad view not available, falling back to egresoCab:', viewError);
        
        // Fallback to egresoCab if view doesn't exist
        const startDateStr = validStartDate.toISOString().split('T')[0];
        const endDateStr = validEndDate.toISOString().split('T')[0];
        
        let filter = `fecha >= "${startDateStr}" && fecha < "${endDateStr}"`;
        
        if (searchQuery.trim()) {
          const search = searchQuery.trim().replace(/[\\"]/g, '\\$&');
          const searchFilter = `numero ~ "${search}" || nombre ~ "${search}" || observ ~ "${search}" || delivery_method ~ "${search}"`;
          filter = `(${filter}) && (${searchFilter})`;
        }
        
        if (selectedDeliveryMethod !== 'all') {
          filter = `(${filter}) && delivery_method = "${selectedDeliveryMethod}"`;
        }
        
        egresoCabs = await pb.collection('egresoCab').getFullList({
          filter,
          sort: '-updated',
          expand: 'delivery',
          $autoCancel: false
        });
        
        // Transform egresoCab data to match view structure
        // First, get egresoDet data to calculate peso_total correctly
        const egresoDets = await pb.collection('egresoDet').getFullList();
        const egresoDetsByCab = {};
        egresoDets.forEach(det => {
          if (!egresoDetsByCab[det.egresoCab]) {
            egresoDetsByCab[det.egresoCab] = [];
          }
          egresoDetsByCab[det.egresoCab].push(det);
        });

        egresoCabs = egresoCabs.map(egreso => {
          // Calculate peso_total correctly (peso * cantidad for each item)
          const dets = egresoDetsByCab[egreso.id] || [];
          const pesoTotal = dets.reduce((sum, det) => sum + ((det.peso || 0) * (det.cantidad || 0)), 0);
          const lineasDetalle = dets.length;

          return {
            ...egreso,
            numero_pedido: egreso.numero,
            observaciones: egreso.observ,
            metodo_entrega: egreso.delivery_method,
            estado: egreso.sta || 'NOT_READY', // Ensure estado is never undefined
            fecha_estado: egreso.updated,
            operador: 'Sistema',
            tiempo_total_segundos: Math.floor((new Date(egreso.updated) - new Date(egreso.created)) / 1000),
            lineas_detalle: lineasDetalle,
            peso_total: pesoTotal,
            detalles_estados: '',
            delivery_nombre: egreso.expand?.delivery?.nombre || '',
            delivery_telefono: egreso.expand?.delivery?.telefono || '',
            delivery_direccion: egreso.expand?.delivery?.direccion || '',
            pickers: [],
            escoltas: [],
            corredores: []
          };
        });
        
        console.log(`Loaded ${egresoCabs.length} records from egresoCab (fallback)`);
        console.log('Sample egresoCab record:', egresoCabs[0]);
        console.log('Sample sta values:', egresoCabs.slice(0, 5).map(e => ({ id: e.id, sta: e.sta, estado: e.estado })));
      }
      
      if (egresoCabs.length === 0) {
        showToast('No se encontraron registros para el rango de fechas seleccionado', 'info');
        return;
      }
      
      // Update delivery methods
      updateDeliveryMethods();
      
    } catch (err) {
      error = err.message || "Error al cargar los datos";
      console.error("Error in loadData:", err);
      showToast(error, 'error');
    } finally {
      isLoading = false;
    }
  }
  
  // Update unique delivery methods
  function updateDeliveryMethods() {
    const methods = new Set(['all']);
    egresoCabs.forEach(egreso => {
      if (egreso.metodo_entrega) {
        methods.add(egreso.metodo_entrega);
      }
    });
    uniqueDeliveryMethods = Array.from(methods);
  }
  
  // Filter egresos
  function filterEgresoCabs() {
    let filtered = egresoCabs;
    
    if (selectedDeliveryMethod !== 'all') {
      filtered = filtered.filter(egreso => egreso.metodo_entrega === selectedDeliveryMethod);
    }
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(egreso => {
        return egreso.numero_pedido?.toString().toLowerCase().includes(query) ||
               egreso.fecha?.toLowerCase().includes(query) ||
               egreso.observaciones?.toLowerCase().includes(query) ||
               egreso.nombre?.toLowerCase().includes(query) ||
               egreso.metodo_entrega?.toLowerCase().includes(query) ||
               egreso.estado?.toLowerCase().includes(query) ||
               egreso.delivery_nombre?.toLowerCase().includes(query) ||
               egreso.detalles_estados?.toLowerCase().includes(query) ||
               parseJsonField(egreso.pickers).some(p => p.toLowerCase().includes(query)) ||
               parseJsonField(egreso.escoltas).some(e => e.toLowerCase().includes(query)) ||
               parseJsonField(egreso.corredores).some(c => c.toLowerCase().includes(query));
      });
    }
    
    filteredEgresoCabs = filtered;
    currentPage = 1;
    updatePagination();
  }
  
  // Update pagination (for server-side pagination, this just sets paginatedEgresoCabs to the loaded data)
  function updatePagination() {
    // With server-side pagination, egresoCabs already contains the correct page data
    paginatedEgresoCabs = egresoCabs;
  }
  
  // Change page
  function changePage(newPage) {
    console.log('changePage called:', newPage, 'current:', currentPage, 'totalPages:', totalPages);
    if (newPage >= 1 && newPage <= totalPages) {
      currentPage = newPage;
      console.log('Page changed to:', currentPage);
      loadData(); // Reload data for the new page
    }
  }
  
  // Handle item click
  async function handleItemClick(egreso) {
    try {
      // Fetch the original egresoCab data to get all fields including correo, nit, observ
      const originalEgreso = await pb.collection('egresoCab').getOne(egreso.id);
      
      // Map egreso_trazabilidad view fields to expected field names for DeliveryDetails
      selectedItem = {
        ...originalEgreso, // Use original egresoCab data as base
        // Override with view data where available
        estado: egreso.estado,
        observaciones: egreso.observaciones,
        numero_pedido: egreso.numero_pedido,
        metodo_entrega: egreso.metodo_entrega,
        // Map estado to sta for status
        sta: egreso.estado,
        // Map observaciones to observ for observations
        observ: egreso.observaciones,
        // Map numero_pedido to numero for order number
        numero: egreso.numero_pedido,
        // Map metodo_entrega to delivery_method for delivery method
        delivery_method: egreso.metodo_entrega,
        // Parse pickers from JSON string to array format expected by DeliveryDetails
        pickers: egreso.pickers && egreso.pickers !== 'JSON' ? parseJsonField(egreso.pickers) : [],
        // Parse corredores from JSON string to array format expected by DeliveryDetails
        corredores: egreso.corredores && egreso.corredores !== 'JSON' ? parseJsonField(egreso.corredores) : [],
        // Parse escoltas from JSON string to array format expected by DeliveryDetails
        escoltas: egreso.escoltas && egreso.escoltas !== 'JSON' ? parseJsonField(egreso.escoltas) : []
      };
      showRightPane = true;
    } catch (error) {
      console.error('Error fetching original egresoCab data:', error);
      // Fallback to view data only
      selectedItem = {
        ...egreso,
        // Map estado to sta for status
        sta: egreso.estado,
        // Map observaciones to observ for observations
        observ: egreso.observaciones,
        // Map numero_pedido to numero for order number
        numero: egreso.numero_pedido,
        // Map metodo_entrega to delivery_method for delivery method
        delivery_method: egreso.metodo_entrega,
        // Ensure correo field is available (may be undefined from view)
        correo: egreso.correo || '',
        // Ensure nit field is available (may be undefined from view)
        nit: egreso.nit || '',
        // Parse pickers from JSON string to array format expected by DeliveryDetails
        pickers: egreso.pickers && egreso.pickers !== 'JSON' ? parseJsonField(egreso.pickers) : [],
        // Parse corredores from JSON string to array format expected by DeliveryDetails
        corredores: egreso.corredores && egreso.corredores !== 'JSON' ? parseJsonField(egreso.corredores) : [],
        // Parse escoltas from JSON string to array format expected by DeliveryDetails
        escoltas: egreso.escoltas && egreso.escoltas !== 'JSON' ? parseJsonField(egreso.escoltas) : []
      };
      showRightPane = true;
    }
  }
  
  // Close right pane
  function closeRightPane() {
    showRightPane = false;
    selectedItem = null;
  }
  
  // Export to Excel
  async function exportToExcel() {
    isExporting = true;
    try {
      // Use the same filters as the current view
      console.log('Fetching filtered data for export...');
      
      // Build the same filter as loadData()
      const validStartDate = new Date(formattedStartDate);
      const validEndDate = new Date(formattedEndDate);
      validStartDate.setHours(0, 0, 0, 0);
      validEndDate.setHours(23, 59, 59, 999);
      
      const startDateStr = validStartDate.toISOString().split('T')[0];
      const endDateStr = validEndDate.toISOString().split('T')[0];
      
      // Use fecha field from egresoCab (the original date field)
      let filter = `fecha >= "${startDateStr}" && fecha < "${endDateStr}"`;
      
      if (searchQuery.trim()) {
        const search = searchQuery.trim().replace(/[\\"]/g, '\\$&');
        const searchFilter = `numero_pedido ~ "${search}" || nombre ~ "${search}" || observaciones ~ "${search}" || metodo_entrega ~ "${search}"`;
        filter = `(${filter}) && (${searchFilter})`;
      }
      
      if (selectedDeliveryMethod !== 'all') {
        filter = `(${filter}) && metodo_entrega = "${selectedDeliveryMethod}"`;
      }
      
      // Export from egresoCab (view egreso_trazabilidad returns 400 when filter is sent — see CORRECTED_SQL_QUERY.sql)
      const cabFilter = `fecha >= "${startDateStr}" && fecha < "${endDateStr}"`;
      const search = searchQuery.trim().replace(/[\\"]/g, '\\$&');
      const cabFilterFull = search
        ? `(${cabFilter}) && (numero ~ "${search}" || nombre ~ "${search}" || observ ~ "${search}" || delivery_method ~ "${search}")`
        : cabFilter;
      const cabFilterFinal = selectedDeliveryMethod !== 'all'
        ? `(${cabFilterFull}) && delivery_method = "${selectedDeliveryMethod}"`
        : cabFilterFull;
      const cabList = await pb.collection('egresoCab').getFullList({
        filter: cabFilterFinal,
        sort: '-updated',
        $autoCancel: false
      });
      const cabIds = cabList.map(c => c.id);
      const egresoDetsForCab = {};
      const BATCH = 40;
      for (let i = 0; i < cabList.length; i += BATCH) {
        const chunk = cabList.slice(i, i + BATCH).map(c => c.id);
        const f = chunk.map(id => `egresoCab = "${id}"`).join(' || ');
        try {
          const batch = await pb.collection('egresoDet').getFullList({ filter: f, $autoCancel: false });
          batch.forEach(det => {
            if (!egresoDetsForCab[det.egresoCab]) egresoDetsForCab[det.egresoCab] = [];
            egresoDetsForCab[det.egresoCab].push(det);
          });
        } catch (_) {}
      }
      // Pickers: egresoPicker -> expand picker (user: name or email)
      const pickersByCab = {};
      cabIds.forEach(id => { pickersByCab[id] = []; });
      try {
        for (let i = 0; i < cabIds.length; i += BATCH) {
          const chunk = cabIds.slice(i, i + BATCH);
          const f = chunk.map(id => `egresoCab = "${id}"`).join(' || ');
          const list = await pb.collection('egresoPicker').getFullList({ filter: f, expand: 'picker', $autoCancel: false });
          list.forEach(p => {
            const name = p.expand?.picker?.name || p.expand?.picker?.email || '';
            if (name && pickersByCab[p.egresoCab]) pickersByCab[p.egresoCab].push(name);
          });
        }
      } catch (_) {}
      // Corredores: egresoCorredor -> expand corredor (nombre)
      const corredoresByCab = {};
      cabIds.forEach(id => { corredoresByCab[id] = []; });
      try {
        for (let i = 0; i < cabIds.length; i += BATCH) {
          const chunk = cabIds.slice(i, i + BATCH);
          const f = chunk.map(id => `egresoCab = "${id}"`).join(' || ');
          const list = await pb.collection('egresoCorredor').getFullList({ filter: f, expand: 'corredor', $autoCancel: false });
          list.forEach(c => {
            const nombre = c.expand?.corredor?.nombre || '';
            if (nombre && corredoresByCab[c.egresoCab]) corredoresByCab[c.egresoCab].push(nombre);
          });
        }
      } catch (_) {}
      // Delivery: egresoDrop with expand delivery
      const deliveryByCab = {};
      cabIds.forEach(id => { deliveryByCab[id] = {}; });
      const dropIdToCab = {};
      try {
        for (let i = 0; i < cabIds.length; i += BATCH) {
          const chunk = cabIds.slice(i, i + BATCH);
          const f = chunk.map(id => `egresoCab = "${id}"`).join(' || ');
          const drops = await pb.collection('egresoDrop').getFullList({
            filter: f,
            expand: 'delivery',
            $autoCancel: false
          });
          drops.forEach(drop => {
            dropIdToCab[drop.id] = drop.egresoCab;
            if (!deliveryByCab[drop.egresoCab].nombre && drop.expand?.delivery) {
              deliveryByCab[drop.egresoCab] = {
                nombre: drop.expand.delivery.nombre ?? '',
                telefono: drop.expand.delivery.telefono ?? '',
                direccion: drop.expand.delivery.direccion ?? ''
              };
            }
          });
        }
      } catch (_) {}
      // Escoltas: egresoDropEscolta filtered by egresoDrop (from drops), expand escolta
      const escoltasByCab = {};
      cabIds.forEach(id => { escoltasByCab[id] = []; });
      try {
        const dropIds = Object.keys(dropIdToCab);
        for (let i = 0; i < dropIds.length; i += BATCH) {
          const chunk = dropIds.slice(i, i + BATCH);
          const f = chunk.map(id => `egresoDrop = "${id}"`).join(' || ');
          const list = await pb.collection('egresoDropEscolta').getFullList({
            filter: f,
            expand: 'escolta',
            $autoCancel: false
          });
          list.forEach(ede => {
            const cabId = dropIdToCab[ede.egresoDrop];
            if (!cabId) return;
            const esc = ede.expand?.escolta ?? ede.escolta;
            const name = (esc?.name || esc?.email || esc?.nombre || '').toString().trim();
            if (name && escoltasByCab[cabId]) escoltasByCab[cabId].push(name);
          });
        }
        cabIds.forEach(id => { escoltasByCab[id] = [...new Set(escoltasByCab[id] || [])]; });
      } catch (_) {}
      // Detalle de estados: logs (collection=egresoCab, record_id=cab), sort by created, format
      const detallesByCab = {};
      cabIds.forEach(id => { detallesByCab[id] = ''; });
      try {
        for (let i = 0; i < cabIds.length; i += BATCH) {
          const chunk = cabIds.slice(i, i + BATCH);
          const f = `collection = "egresoCab" && (${chunk.map(id => `record_id = "${id}"`).join(' || ')})`;
          const logs = await pb.collection('logs').getFullList({
            filter: f,
            sort: 'created',
            $autoCancel: false
          });
          const logsByCab = {};
          logs.forEach(log => {
            if (!log.new_status) return;
            if (!logsByCab[log.record_id]) logsByCab[log.record_id] = [];
            logsByCab[log.record_id].push(log);
          });
          Object.keys(logsByCab).forEach(cabId => {
            const sorted = logsByCab[cabId].sort((a, b) => new Date(a.created) - new Date(b.created));
            const statusText = (s) => getStatusText(s) || s;
            const dateStr = (d) => {
              const dt = new Date(d);
              return dt.toLocaleString('es-GT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
            };
            detallesByCab[cabId] = sorted.map(l => `${statusText(l.new_status)} (${dateStr(l.created)})`).join(' → ');
          });
        }
      } catch (_) {}
      const allData = cabList.map(egreso => {
        const dets = egresoDetsForCab[egreso.id] || [];
        const pesoTotal = dets.reduce((s, d) => s + ((d.peso || 0) * (d.cantidad || 0)), 0);
        const delivery = deliveryByCab[egreso.id] || {};
        return {
          ...egreso,
          numero_pedido: egreso.numero,
          observaciones: egreso.observ,
          metodo_entrega: egreso.delivery_method,
          estado: egreso.sta || 'NOT_READY',
          fecha_estado: egreso.updated,
          operador: 'Sistema',
          tiempo_total_segundos: Math.floor((new Date(egreso.updated) - new Date(egreso.created)) / 1000),
          lineas_detalle: dets.length,
          peso_total: pesoTotal,
          detalles_estados: detallesByCab[egreso.id] ?? '',
          delivery_nombre: delivery.nombre ?? egreso.expand?.delivery?.nombre ?? '',
          delivery_telefono: delivery.telefono ?? egreso.expand?.delivery?.telefono ?? '',
          delivery_direccion: delivery.direccion ?? egreso.expand?.delivery?.direccion ?? '',
          pickers: pickersByCab[egreso.id] || [],
          escoltas: escoltasByCab[egreso.id] || [],
          corredores: corredoresByCab[egreso.id] || []
        };
      });
      console.log(`Exporting ${allData.length} filtered records (from egresoCab)`);
      const correctedData = allData;
      
      const wb = XLSX.utils.book_new();
      const wsData = [
        ['Número de Pedido', 'Fecha del Pedido', 'Nombre', 'Observaciones', 'Método de Entrega', 'Pickers', 'Escoltas', 'Corredores', 'Estado', 'Fecha del Estado', 'Operador', 'Tiempo Total (Segundos)', 'Líneas de Detalle', 'Peso Total', 'Detalles de Estados', 'Delivery Nombre', 'Delivery Teléfono', 'Delivery Dirección']
      ];
      
      const rows = correctedData.map(egreso => {
        const pickers = parseJsonField(egreso.pickers).join(', ');
        const escoltas = parseJsonField(egreso.escoltas).join(', ');
        const corredores = parseJsonField(egreso.corredores).join(', ');
        
        return [
          egreso.numero_pedido || '',
          formatDate(egreso.fecha) || '',
          egreso.nombre || '',
          egreso.observaciones || '',
          egreso.metodo_entrega || '',
          pickers,
          escoltas,
          corredores,
          getStatusText(egreso.estado),
          formatDate(egreso.fecha_estado),
          egreso.operador || 'Sistema',
          egreso.tiempo_total_segundos || 0,
          egreso.lineas_detalle || 0,
          egreso.peso_total || 0,
          egreso.detalles_estados || '',
          egreso.delivery_nombre && egreso.delivery_nombre !== 'JSON' ? egreso.delivery_nombre : '',
          egreso.delivery_telefono && egreso.delivery_telefono !== 'JSON' ? egreso.delivery_telefono : '',
          egreso.delivery_direccion && egreso.delivery_direccion !== 'JSON' ? egreso.delivery_direccion : ''
        ];
      });
      
      wsData.push(...rows);
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      
      const colWidths = [
        { wch: 15 }, { wch: 20 }, { wch: 30 }, { wch: 40 }, { wch: 20 },
        { wch: 30 }, { wch: 30 }, { wch: 30 }, { wch: 20 },
        { wch: 25 }, { wch: 20 }, { wch: 15 }, { wch: 15 },
        { wch: 15 }, { wch: 100 }, { wch: 30 }, { wch: 20 }, { wch: 40 }
      ];
      ws['!cols'] = colWidths;
      
      XLSX.utils.book_append_sheet(wb, ws, 'Timeline');
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `timeline_export_${currentDate}.xlsx`;
      XLSX.writeFile(wb, filename);
      
      showToast(`Archivo Excel generado exitosamente con ${correctedData.length} registros filtrados`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      let msg = error?.message || String(error);
      try {
        const parsed = JSON.parse(msg);
        if (parsed?.message) msg = parsed.message;
        if (parsed?.status) msg = `[${parsed.status}] ${msg}`;
      } catch (_) { /* keep msg as is */ }
      showToast(`Error al generar el archivo Excel: ${msg}`, 'error');
    } finally {
      isExporting = false;
    }
  }
  
  // Show toast
  function showToast(message, type = 'success') {
    toasts.add({
      title: type === 'success' ? 'Éxito' : type === 'error' ? 'Error' : 'Información',
      description: message,
      duration: type === 'error' ? 5000 : 3000,
      placement: 'bottom-right',
      type: type,
      theme: 'dark',
      showProgress: true
    });
  }
  
  // Reactive statements
  // Update paginated data when egresoCabs changes (server-side pagination)
  $: if (egresoCabs) {
    paginatedEgresoCabs = egresoCabs;
  }

  // Track previous values to detect actual changes
  let prevSearchQuery = '';
  let prevSelectedDeliveryMethod = 'all';
  
  // Handle filter changes manually
  function handleFilterChange() {
    console.log('Filter change detected - search:', searchQuery, 'delivery:', selectedDeliveryMethod);
    if (searchQuery !== prevSearchQuery || selectedDeliveryMethod !== prevSelectedDeliveryMethod) {
      console.log('Filter changed, resetting to page 1');
      prevSearchQuery = searchQuery;
      prevSelectedDeliveryMethod = selectedDeliveryMethod;
      currentPage = 1; // Reset to first page when filters change
      loadData(); // Reload data from server with new filters
    }
  }
  
  // Load data on mount
  onMount(() => {
    loadData();
  });
</script>

<style>
  .timeline-container {
    padding: 20px;
    background-color: #f8f9fa;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  }

  .timeline {
    display: flex;
    position: relative;
    padding: 1rem 0;
  }

  @media (min-width: 768px) {
    .timeline {
      flex-direction: row;
      overflow-x: auto;
      padding-bottom: 2rem;
    }

    .timeline-item {
      flex: 0 0 300px;
      margin-right: 2rem;
      position: relative;
    }

    .timeline-item::after {
      content: '';
      position: absolute;
      top: 50%;
      right: -1.5rem;
      width: 1rem;
      height: 2px;
      background: #c5c9d0;
    }

    .timeline-item:last-child::after {
      display: none;
    }

    .timeline::before {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 2px;
      background: #c5c9d0;
      z-index: 0;
    }
  }

  @media (max-width: 767px) {
    .timeline {
      flex-direction: column;
      padding-left: 2rem;
    }

    .timeline-item {
      margin-bottom: 1.5rem;
      position: relative;
    }

    .timeline-item::before {
      content: '';
      position: absolute;
      left: -2rem;
      top: 0;
      bottom: 0;
      width: 2px;
      background: #c5c9d0;
    }

    .timeline-item::after {
      content: '';
      position: absolute;
      left: -2.35rem;
      top: 1rem;
      width: 0.75rem;
      height: 0.75rem;
      border-radius: 50%;
      background: #3b82f6;
      border: 2px solid white;
      z-index: 1;
    }
  }

  .timeline-item {
    padding: 1rem;
    border-radius: 8px;
    background: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    cursor: pointer;
  }

  .timeline-item:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .timeline-header {
    padding-bottom: 0.5rem;
    margin-bottom: 0.5rem;
    border-bottom: 1px solid #eee;
    font-weight: 600;
    color: #333;
  }

  .timeline-content p {
    margin: 0.4rem 0;
    color: #555;
    font-size: 0.95rem;
  }

  .status-badge {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    margin-top: 0.5rem;
    border-width: 1px;
    border-style: solid;
  }

  .timestamp {
    font-size: 0.8rem;
    color: #777;
    margin-top: 0.75rem;
    display: block;
  }

  .pagination {
    display: flex;
    justify-content: center;
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid #eee;
  }

  .pagination-btn {
    padding: 0.5rem 1rem;
    margin: 0 0.25rem;
    border-radius: 4px;
    font-size: 0.875rem;
    background: white;
    border: 1px solid #ddd;
    cursor: pointer;
    transition: all 0.2s;
  }

  .pagination-btn:hover {
    background: #f8f9fa;
    border-color: #c5c9d0;
  }

  .pagination-btn.active {
    background: #3b82f6;
    color: white;
    border-color: #3b82f6;
  }

  .pagination-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .pagination-info {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    color: #6b7280;
    font-weight: 500;
  }

  .search-container {
    margin-bottom: 1.5rem;
    position: relative;
  }

  .search-input {
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 2.5rem;
    border: 1px solid #e2e8f0;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    transition: border-color 0.15s ease;
  }

  .search-input:focus {
    border-color: #3b82f6;
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  .search-icon {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #a0aec0;
  }

  .export-excel-btn {
    background-color: #2563eb;
  }
  .export-excel-btn:hover {
    background-color: #1d4ed8;
  }

  .picker-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }

  .picker-badge {
    padding: 0.25rem 0.5rem;
    background-color: #e2e8f0;
    border-radius: 0.25rem;
    font-size: 0.75rem;
    color: #4a5568;
  }

  .status-transitions {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-left: 3px solid #6c757d;
    padding: 0.75rem;
    border-radius: 0.375rem;
    margin-top: 0.75rem;
  }

  .status-transitions .title {
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.5rem;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .status-transitions .content {
    color: #6c757d;
    line-height: 1.4;
    font-size: 0.75rem;
  }

  /* Timeline container with main card and status cards */
  .timeline {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
  }

  .timeline-item {
    flex: 1;
  }

  .timeline-content {
    width: 100%;
  }

  .status-cards-right {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 1rem;
  }

  .status-card-individual {
    border-radius: 0.5rem;
    padding: 1rem 1.5rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
    min-width: 12rem;
    text-align: center;
  }

  .status-card-individual:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  .status-text {
    font-weight: 700;
    font-size: 0.875rem;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    margin-bottom: 0.5rem;
  }

  .status-timestamp {
    font-size: 0.75rem;
    opacity: 0.8;
    font-weight: 500;
  }

  /* Personnel badges styling */
  .personnel-row {
    display: flex;
    align-items: center;
    margin: 0.5rem 0;
    flex-wrap: wrap;
    gap: 0.25rem;
  }

  .personnel-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #6b7280;
    margin-right: 0.5rem;
    min-width: fit-content;
  }

  .personnel-badge {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    border-radius: 0.375rem;
    font-size: 0.75rem;
    font-weight: 500;
    background-color: #f3f4f6;
    color: #374151;
    border: 1px solid #d1d5db;
  }

  .personnel-badge.escolta {
    background-color: #fce7f3;
    color: #be185d;
    border-color: #f9a8d4;
  }

  .personnel-badge.corredor {
    background-color: #ecfdf5;
    color: #065f46;
    border-color: #86efac;
  }

  .personnel-badge.delivery {
    background-color: #eff6ff;
    color: #1e40af;
    border-color: #93c5fd;
  }

  .personnel-badge.phone {
    background-color: #f9fafb;
    color: #6b7280;
    border-color: #d1d5db;
  }

  /* Status color variations - matching RightPane colors */
  .status-card-current.bg-red-200 {
    background-color: #fecaca;
    border-color: #fca5a5;
    color: #991b1b;
  }

  .status-card-current.bg-yellow-500 {
    background-color: #eab308;
    border-color: #ca8a04;
    color: #713f12;
  }

  .status-card-current.bg-orange-200 {
    background-color: #fed7aa;
    border-color: #fdba74;
    color: #9a3412;
  }

  .status-card-current.bg-teal-200 {
    background-color: #99f6e4;
    border-color: #5eead4;
    color: #134e4a;
  }

  .status-card-current.bg-lightBlue-200 {
    background-color: #bae6fd;
    border-color: #7dd3fc;
    color: #0c4a6e;
  }

  .status-card-current.bg-emerald-200 {
    background-color: #a7f3d0;
    border-color: #6ee7b7;
    color: #064e3b;
  }

  .status-card-current.bg-gray-100 {
    background-color: #f3f4f6;
    border-color: #d1d5db;
    color: #374151;
  }

  :global(.svelte-toasts) {
    height: auto !important;
  }
  :global(.svelte-toast) {
    margin: 0.5em;
    padding: 0.75em 1em;
    border-radius: 0.25em;
    font-size: 0.875rem;
  }
  :global(.svelte-toast.success) {
    background: #10B981;
    color: white;
  }
  :global(.svelte-toast.error) {
    background: #EF4444;
    color: white;
  }
</style>

<div class="relative md:pt-6 pb-4 flex flex-col justify-between items-start">
    <div class="timeline-container w-full">
        <div class="flex justify-between items-center mb-4">
            <h2 class="text-xl font-semibold">Trazabilidad de pedidos</h2>
            <button 
                on:click={exportToExcel}
                class="export-excel-btn px-4 py-2 text-white rounded transition-colors"
                disabled={isExporting}
            >
                {#if isExporting}
                    <i class="fas fa-spinner fa-spin mr-2"></i>Preparando datos...
                {:else}
                    <i class="fas fa-file-excel mr-2"></i>Exportar a Excel
                {/if}
            </button>
        </div>
        
        <!-- Search and filters -->
        <div class="search-container">
            <div class="flex flex-col md:flex-row gap-4 mb-4">
                <div class="flex-1">
                    <label for="search-input" class="text-sm text-blueGray-500 mb-1">Buscar</label>
                    <div class="relative">
                        <span class="search-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </span>
                        <input 
                            id="search-input"
                            type="text" 
                            bind:value={searchQuery} 
                            on:input={handleFilterChange}
                            placeholder="Número, nombre, observaciones..."
                            class="search-input"
                        />
                        {#if searchQuery}
                            <button 
                                class="absolute right-16 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                on:click={() => { searchQuery = ""; handleFilterChange(); }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        {/if}
                    </div>
                </div>
                <div class="flex gap-4">
                    <div class="flex flex-col">
                        <label for="delivery-method" class="text-sm text-gray-600 mb-1">Método de entrega</label>
                        <select 
                            id="delivery-method"
                            bind:value={selectedDeliveryMethod}
                            on:change={handleFilterChange}
                            class="border rounded px-3 py-2 text-sm"
                        >
                            {#each uniqueDeliveryMethods as method}
                                <option value={method}>
                                    {method === 'all' ? 'Todos' : method}
                                </option>
                            {/each}
                        </select>
                    </div>
                    <div class="flex flex-col">
                        <label for="start-date" class="text-sm text-gray-600 mb-1">Desde</label>
                        <input 
                            id="start-date"
                            type="date" 
                            bind:value={formattedStartDate}
                            max={formattedEndDate}
                            class="border rounded px-3 py-2 text-sm"
                        />
                    </div>
                    <div class="flex flex-col">
                        <label for="end-date" class="text-sm text-gray-600 mb-1">Hasta</label>
                        <input 
                            id="end-date"
                            type="date" 
                            bind:value={formattedEndDate}
                            min={formattedStartDate}
                            class="border rounded px-3 py-2 text-sm"
                        />
                    </div>
                    <div class="flex flex-col">
                        <label for="search-button" class="text-sm text-gray-600 mb-1">&nbsp;</label>
                        <button 
                            id="search-button"
                            on:click={loadData}
                            disabled={isLoading}
                            class="bg-blueGray-600 hover:bg-blueGray-600 disabled:bg-gray-400 text-white px-4 py-2 rounded text-sm font-medium transition-colors flex items-center gap-2"
                        >
                            {#if isLoading}
                                <i class="fas fa-spinner fa-spin"></i>
                                Cargando...
                            {:else}
                                <i class="fas fa-search"></i>
                                Buscar
                            {/if}
                        </button>
                    </div>
                </div>
            </div>
        </div>
        
        {#if isLoading}
            <div class="p-4 text-center">
                <div class="mb-4">
                    <i class="fas fa-spinner fa-spin text-2xl text-blue-500 mb-2"></i>
                    <p>Cargando datos desde vista optimizada...</p>
                </div>
            </div>
        {:else if error}
            <div class="p-4 text-center text-red-500">
                <p>{error}</p>
            </div>
        {:else if egresoCabs.length === 0}
            <div class="p-4 text-center">
                <p>No se encontraron egresos para mostrar.</p>
            </div>
        {:else}
            <!-- Summary -->
            <div class="mb-4 text-sm text-gray-600">
                Mostrando {paginatedEgresoCabs.length} de {totalItems} egresos
                {#if searchQuery}
                    <span>para "{searchQuery}"</span>
                {/if}
            </div>
            
            <!-- Timeline for each egreso -->
            {#each paginatedEgresoCabs || [] as egreso}
                <div class="mb-8">
                    <h3 class="text-lg font-semibold mb-3">
                        {egreso.nombre} 
                        {#if egreso.observaciones}
                            - {egreso.observaciones}
                        {/if}
                    </h3>
                    
                    <div class="text-sm text-gray-600 mb-2">
                        <strong>Fecha del pedido:</strong> {formatDate(egreso.fecha)}
                    </div>
                    
                    <div class="text-sm text-gray-600 mb-2">
                        Tiempo total: {formatTotalTime(egreso.tiempo_total_segundos || 0)}
                    </div>
                    
                    <div class="timeline">
                        <div class="timeline-item" on:click={() => handleItemClick(egreso)}>
                            <div class="timeline-header">
                                Pedido #{egreso.numero_pedido}
                            </div>
                            <div class="timeline-content">
                                <p><strong>Método de entrega:</strong> {egreso.metodo_entrega || 'No especificado'}</p>
                                <p><strong>Operado por:</strong> {egreso.operador || 'Sistema'}</p>
                                <p><strong>Líneas de detalle:</strong> {egreso.lineas_detalle || 0}</p>
                                <p><strong>Peso total:</strong> {egreso.peso_total || 0} Lb</p>
                                
                                <!-- Pickers -->
                                {#if egreso.pickers && egreso.pickers !== 'JSON'}
                                    <div class="personnel-row">
                                        <span class="personnel-label">Pickers:</span>
                                        <!-- Debug: {egreso.pickers} -->
                                        {#each parseJsonField(egreso.pickers) as picker}
                                            <span class="personnel-badge">{picker}</span>
                                        {/each}
                                    </div>
                                {/if}
                                
                                <!-- Escoltas -->
                                {#if egreso.escoltas && egreso.escoltas !== 'JSON'}
                                    <div class="personnel-row">
                                        <span class="personnel-label">Escoltas:</span>
                                        {#each parseJsonField(egreso.escoltas) as escolta}
                                            <span class="personnel-badge escolta">{escolta}</span>
                                        {/each}
                                    </div>
                                {/if}
                                
                                <!-- Corredores -->
                                {#if egreso.corredores && egreso.corredores !== 'JSON'}
                                    <div class="personnel-row">
                                        <span class="personnel-label">Corredores:</span>
                                        <!-- Debug: {egreso.corredores} -->
                                        {#each parseJsonField(egreso.corredores) as corredor}
                                            <span class="personnel-badge corredor">{corredor}</span>
                                        {/each}
                                    </div>
                                {/if}
                                
                                <!-- Piloto -->
                                {#if egreso.delivery_nombre && egreso.delivery_nombre !== 'JSON'}
                                    <div class="personnel-row">
                                        <span class="personnel-label">Piloto:</span>
                                        <span class="personnel-badge delivery">{egreso.delivery_nombre}</span>
                                    </div>
                                {/if}
                                
                                <div class="status-badge {getStatusColor(egreso.estado)}">
                                    {getStatusText(egreso.estado)}
                                </div>
                                <span class="timestamp">
                                    {formatTimestampForDisplay(egreso.fecha_estado)}
                                </span>
                            </div>
                        </div>
                        
                        {#if egreso.detalles_estados}
                            <div class="status-cards-right">
                                {#each parseStatusTransitions(egreso.detalles_estados) as transition}
                                    <div class="status-card-individual {getStatusColor(transition.status)}">
                                        <div class="status-text">
                                            {getStatusText(transition.status)}
                                        </div>
                                        <div class="status-timestamp">
                                            {formatTimestampForDisplay(transition.timestamp)}
                                        </div>
                                    </div>
                                {/each}
                            </div>
                        {/if}
                    </div>
                </div>
            {/each}

            <!-- Pagination -->
            {#if totalPages > 1}
                <div class="pagination">
                    <button 
                        class="pagination-btn" 
                        on:click={() => changePage(currentPage - 1)} 
                        disabled={currentPage === 1}>
                        ← Anterior
                    </button>
                    
                    <span class="pagination-info">
                        Página {currentPage} de {totalPages}
                    </span>
                    
                    <button 
                        class="pagination-btn" 
                        on:click={() => {
                            console.log('Next button clicked - currentPage:', currentPage, 'going to:', currentPage + 1);
                            changePage(currentPage + 1);
                        }} 
                        disabled={currentPage === totalPages}>
                        Siguiente →
                    </button>
                </div>
            {/if}
        {/if}
    </div>
</div>

<!-- Right Pane -->
{#if showRightPane && selectedItem}
    <RightPane 
        selectedItem={selectedItem} 
        closePane={closeRightPane}
        status={selectedItem.sta}
    />
{/if}